import type { ReactNode, TextareaHTMLAttributes } from 'react';

import { cn } from '@/lib/cn';

import { CONTROL_CLASS, FieldShell } from './Field';

export interface TextareaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  /** Shows "n / max" under the box. Pair with `maxLength`. */
  showCount?: boolean;
  wrapperClassName?: string;
}

export function Textarea({
  label,
  hint,
  error,
  showCount = false,
  required,
  rows = 4,
  maxLength,
  value,
  className,
  wrapperClassName,
  ...rest
}: TextareaProps) {
  const length = typeof value === 'string' ? value.length : 0;
  const counter =
    showCount && maxLength ? `${length} / ${maxLength}` : undefined;

  return (
    <FieldShell
      label={label}
      hint={counter ?? hint}
      error={error}
      required={required}
      className={wrapperClassName}
    >
      {({ id, describedBy, invalid }) => (
        <textarea
          id={id}
          rows={rows}
          value={value}
          maxLength={maxLength}
          required={required}
          aria-invalid={invalid}
          aria-describedby={describedBy}
          className={cn(
            CONTROL_CLASS,
            'resize-y py-2 leading-relaxed',
            invalid ? 'border-danger' : 'border-line-strong hover:border-ink/25',
            className,
          )}
          {...rest}
        />
      )}
    </FieldShell>
  );
}
